import { useApiMutation } from "@gladis/api-client";
import { useState, type ReactNode } from "react";
import { Button } from "@gladis/ui";
import { errorSentence, useT } from "@gladis/ui/i18n";

/**
 * Leaving a household (AC-H6).
 *
 * The Owner does not get this. They get an explanation of the two ways out instead, because a
 * disabled button teaches nobody anything — and "you cannot leave" without "transfer ownership or
 * delete the household" is a dead end rather than a rule.
 *
 * The refusal also exists server-side in `MembershipService.canLeave`, which returns the same two
 * options in its message. This component reproduces them rather than rendering the server's error,
 * because the Owner should see the explanation BEFORE trying, not after being refused.
 */
export function LeaveHouseholdDialog({
  householdId,
  householdName,
  isOwner,
  onLeft,
}: {
  householdId: string;
  householdName: string;
  isOwner: boolean;
  onLeft: () => void;
}): ReactNode {
  const t = useT();
  const [confirming, setConfirming] = useState(false);

  const leave = useApiMutation<void, void>(
    (client) => client.post(`/households/${householdId}/leave`),
    { discardsEverything: true, onSuccess: onLeft },
  );

  if (isOwner) {
    return (
      <section className="mt-10 rounded-control border border-line bg-panel p-5">
        <h2 className="text-xl font-semibold">Leaving {householdName}</h2>
        <p className="mt-2 text-muted">{t("identity.leave.ownerCannot")}</p>
      </section>
    );
  }

  return (
    <section className="mt-10 rounded-control border border-line bg-panel p-5">
      <h2 className="text-xl font-semibold">Leave {householdName}</h2>

      {confirming ? (
        <div className="mt-3" role="alert">
          <p>{t("identity.leave.immediate")}</p>
          <p className="mt-2 text-sm text-muted">{t("identity.leave.consequence")}</p>

          <div className="mt-4 flex gap-3">
            <Button
              variant="primary"
              type="button"
              onClick={() => leave.mutate()}
              disabled={leave.isPending}
            >
              {leave.isPending ? t("identity.leave.confirm") : t("identity.leave.confirm")}
            </Button>
            <Button type="button" onClick={() => setConfirming(false)}>
              {t("common.cancel")}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <p className="mt-2 text-muted">{t("identity.leave.short")}</p>
          <Button className="mt-4" type="button" onClick={() => setConfirming(true)}>
            {t("identity.leave.heading")}
          </Button>
        </>
      )}

      {leave.error ? (
        <p className="mt-3 text-sm text-muted" role="alert">
          {errorSentence(leave.error)}
        </p>
      ) : null}
    </section>
  );
}
